import { Voucher, voucherCertificationTypesEnum } from '../types';
import { Currency } from '../../currencies/types';
import { Client } from '../../clients/types';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../components/Pdfs/PdfComponents/PdfTable';
import { EXCHANGE_RATE_DECIMAL_VIEW_NUMBER, taytanFormatNumberToString } from '../../../utils/mathUtils';
import { formatShortDate } from '../../../utils/dateUtils';
import { formatWallClock } from '../../../utils/wallClock';
import {
    isRateExplicit,
    resolveDocumentLocalRate,
    resolveDocumentPrimaryToLocalRate,
    sameCurrency
} from '../../../utils/currencyMath';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import { resolvePaymentsCount } from '../Services/receiptCardIdentity';

// ── Currency helpers ──────────────────────────────────────────────────────────

export function getCurrencyDetails(
    currencyId: string | Currency | undefined,
    currencies: Currency[]
): { code: string; symbol: string; currency: Currency | null } {
    if (!currencyId) return { code: '---', symbol: '', currency: null };
    const id = typeof currencyId !== 'object' ? currencyId : currencyId._id;
    const currency = currencies.find((c) => c._id === id) || null;
    return { code: currency?.code || '---', symbol: currency?.symbol || '', currency };
}

export function getClientCurrencies(client: Client, currencies: Currency[]) {
    const localCurrency =
        currencies.find((c) => c._id === client?.currencySettings?.localCurrency) || null;
    const primaryCurrency =
        currencies.find((c) => c._id === client?.currencySettings?.primaryCurrency) || null;
    return { localCurrency, primaryCurrency };
}

// ── Remittance amounts table ──────────────────────────────────────────────────

/** Minimal currency shape — callers hold either a full `Currency` or a lookup result. */
type CurrencyLike = { _id?: unknown; code?: string; symbol?: string } | null | undefined;

export interface RemittanceAmountsTableParams {
    /** Net amount in the document's own currency (excludes VAT). */
    amount: number;
    /** VAT charged ON TOP (0 unless this is a "Charge VAT" shekel remittance). */
    vat: number;
    docCurrencyId: unknown;
    docCurrencyCode: string;
    docCurrencySymbol: string;
    localCurrency: CurrencyLike;
    primaryCurrency: CurrencyLike;
    /** The rate stored on the document (`info.rate`/`info.localRate`) or typed into the form. */
    storedRate?: unknown;
    /** Local per 1 PRIMARY unit (₪ per $) the document was issued at: `info.rate`
     *  on a local-currency document, `info.localRate` on a primary one,
     *  `info.primaryRate` otherwise — see `documentPrimaryToLocalSource`. A ₪
     *  remittance stores `localRate` = 1, so passing only `storedRate` lost the
     *  ₪-per-$ rate the user typed and Primary Amount fell back to the market. */
    storedPrimaryRate?: unknown;
    /** `info.amountLocal`, when the document carries one (legacy rows may not). */
    amountLocal?: unknown;
    getConvertRate: (from: any, to: any) => any;
}

/**
 * Build the Remittance "Amount | Tax | Local Price | Rate | Primary Amount"
 * table.
 *
 * Shared deliberately: this table is rendered twice — inline by
 * `Remittance.tsx` for the PDF shown right after creation, and by
 * `buildRemittancePdfInfo` for the PDF you get on reopen. They were two
 * hand-maintained copies of the same arithmetic and had drifted: the inline
 * copy multiplied a shekel total by the ₪↔$ rate (inflating Local Price ~3.5×),
 * and BOTH derived Primary Amount from the live market rate, ignoring the rate
 * the Change Rate dialog had just written. One implementation, one behaviour.
 *
 * Rate semantics live in `resolveDocumentLocalRate` (doc→local; pins a
 * local-currency doc to 1) and `resolveDocumentPrimaryToLocalRate`
 * (primary→local; honours the stored rate when the doc carries it).
 */
export function buildRemittanceAmountsTable(params: RemittanceAmountsTableParams): {
    columns: PdfTablePropsColumn[];
    rows: PdfTablePropsRow[];
    summaryLine: PdfTablePropsRow;
    totalToPay: number;
    localTotal: number;
    primaryAmount: number | null;
    /** Document → local rate used for Local Price. */
    localRate: number;
    /** Local per 1 primary unit used for Primary Amount (0 when unresolved). */
    primaryToLocal: number;
} {
    const {
        docCurrencyId,
        docCurrencyCode,
        docCurrencySymbol,
        localCurrency,
        primaryCurrency,
        storedRate,
        storedPrimaryRate,
        amountLocal,
        getConvertRate
    } = params;

    const amount = Number(params.amount) || 0;
    const vat = Number(params.vat) || 0;
    const round2 = (n: number): number => Math.round(n * 100) / 100;
    // What actually leaves the bank: with "Charge VAT" the VAT sits on top.
    const totalToPay = round2(amount + vat);

    // Last resort is the doc→local MARKET rate, not 1: a foreign remittance that
    // stores no usable rate would otherwise print "Local Price (ILS) 1,000.00 ·
    // Rate 1.00000" for a $1,000 transfer — a wrong number stated confidently.
    const localRate =
        resolveDocumentLocalRate({
            currencyId: docCurrencyId,
            localId: localCurrency?._id,
            storedLocalRate: storedRate,
            amount,
            amountLocal,
            fallbackRate:
                Number(getConvertRate?.(docCurrencyId, localCurrency?._id)?.exchangeRateSell) || 0
        }) ?? 1;
    const localTotal = round2(totalToPay * localRate);

    const marketPrimaryToLocal = sameCurrency(primaryCurrency?._id, localCurrency?._id)
        ? 1
        : Number(getConvertRate?.(primaryCurrency?._id, localCurrency?._id)?.exchangeRateSell) || 0;
    const primaryToLocal = resolveDocumentPrimaryToLocalRate({
        currencyId: docCurrencyId,
        primaryId: primaryCurrency?._id,
        localId: localCurrency?._id,
        storedRate: isRateExplicit(storedPrimaryRate) ? storedPrimaryRate : storedRate,
        storedPrimaryRate,
        marketPrimaryToLocal
    });
    const isLocalDoc = sameCurrency(docCurrencyId, localCurrency?._id);

    // When the document IS in the primary currency the answer is the amount
    // itself — take it directly rather than round-tripping through the local
    // total, which can land a cent off. Matches the Change Rate dialog, which
    // pins the primary row's amount to the document amount in this case.
    const primaryAmount = sameCurrency(docCurrencyId, primaryCurrency?._id)
        ? totalToPay
        : primaryToLocal > 0
          ? round2(localTotal / primaryToLocal)
          : null;

    const localCode = localCurrency?.code || '';
    const primaryCode = primaryCurrency?.code || '';

    const columns: PdfTablePropsColumn[] = [
        { title: `Amount (${docCurrencyCode})`, fieldName: 'amount', style: { width: '20%' } },
        { title: `Tax (${docCurrencyCode})`, fieldName: 'tax', style: { width: '20%' } },
        { title: `Local Price (${localCode})`, fieldName: 'localPrice', style: { width: '22%' } },
        {
            // A local-currency document's own rate is ₪ per ₪ = 1, which says
            // nothing; the rate it was issued at is ₪ per $, so that is shown.
            title: `Rate (${localCode}/${isLocalDoc ? primaryCode : docCurrencyCode})`,
            fieldName: 'rate',
            style: { width: '16%' }
        },
        { title: `Primary Amount (${primaryCode})`, fieldName: 'primaryAmount', style: { width: '22%' } }
    ];

    const rows: PdfTablePropsRow[] = [
        {
            amount: taytanFormatNumberToString(amount),
            tax: taytanFormatNumberToString(vat),
            localPrice: taytanFormatNumberToString(localTotal),
            rate:
                isLocalDoc && primaryToLocal > 0
                    ? taytanFormatNumberToString(primaryToLocal, EXCHANGE_RATE_DECIMAL_VIEW_NUMBER)
                    : taytanFormatNumberToString(localRate, EXCHANGE_RATE_DECIMAL_VIEW_NUMBER),
            primaryAmount: primaryAmount != null ? taytanFormatNumberToString(primaryAmount) : ''
        }
    ];

    const summaryLine: PdfTablePropsRow = {
        amount: `Total ${taytanFormatNumberToString(totalToPay)}${docCurrencySymbol || ''}`,
        tax: '',
        localPrice: `${taytanFormatNumberToString(localTotal)}${localCurrency?.symbol || ''}`,
        rate: '',
        primaryAmount:
            primaryAmount != null
                ? `${taytanFormatNumberToString(primaryAmount)}${primaryCurrency?.symbol || ''}`
                : ''
    };

    return { columns, rows, summaryLine, totalToPay, localTotal, primaryAmount, localRate, primaryToLocal };
}

/**
 * Which stored field holds a document's ₪-per-$ rate. One column cannot: a
 * local-currency document keeps it in `rate` (its localRate is a tautological
 * 1), a primary-currency document in `localRate`, and any other currency in
 * `primaryRate` (stored since 2026-10-28).
 */
export function documentPrimaryToLocalSource(
    info: any,
    localId: unknown,
    primaryId: unknown
): unknown {
    const currencyId = info?.currency && typeof info.currency === 'object' ? info.currency._id : info?.currency;
    if (sameCurrency(currencyId, localId)) return info?.rate;
    if (sameCurrency(currencyId, primaryId)) return info?.localRate ?? info?.rate;
    return info?.primaryRate;
}

/**
 * A covered document's amount in the COVERING document's (remittance / PRQ)
 * currency, for the covered-documents totals. They used to add each document's own amount and label the
 * sum with the remittance's symbol — a $100 PRQ paid by a ₪ RMT totalled
 * "100.00₪". Converted through ₪ at the rates the remittance was issued at: its
 * ₪-per-$ rate for a primary-currency document (the rate the payment was
 * settled at), its own rate for its own currency, and otherwise the covered
 * document's stored rate (market as a last resort).
 */
export function convertCoveredAmountToCovering(params: {
    amount: number;
    docInfo: any;
    coveringCurrencyId: unknown;
    localId: unknown;
    primaryId: unknown;
    coveringLocalRate: number;
    coveringPrimaryToLocal: number;
    getConvertRate?: (from: any, to: any) => any;
}): number {
    const { amount, docInfo, coveringCurrencyId, localId, primaryId } = params;
    const docCurrencyId =
        docInfo?.currency && typeof docInfo.currency === 'object' ? docInfo.currency._id : docInfo?.currency;
    if (!amount || !docCurrencyId || sameCurrency(docCurrencyId, coveringCurrencyId)) return amount;
    const toLocal = (currencyId: unknown, info?: any): number => {
        if (sameCurrency(currencyId, localId)) return 1;
        if (sameCurrency(currencyId, coveringCurrencyId)) return params.coveringLocalRate;
        if (sameCurrency(currencyId, primaryId) && params.coveringPrimaryToLocal > 0) {
            return params.coveringPrimaryToLocal;
        }
        return (
            resolveDocumentLocalRate({
                currencyId,
                localId,
                storedLocalRate: info?.localRate,
                amount: info?.amount,
                amountLocal: info?.amountLocal,
                fallbackRate: Number(params.getConvertRate?.(currencyId, localId)?.exchangeRateSell) || 0
            }) || 0
        );
    };
    const docToLocal = toLocal(docCurrencyId, docInfo);
    const coveringToLocal = toLocal(coveringCurrencyId);
    // Nothing to convert with: the document's own figure, rather than a zero.
    if (!docToLocal || !coveringToLocal) return amount;
    return (amount * docToLocal) / coveringToLocal;
}

// ── Voucher normalization ─────────────────────────────────────────────────────

/** findOne returns array, findById returns object — normalize to object */
export function normalizeVoucherInfo(voucher: Voucher): Voucher {
    if (Array.isArray(voucher.info)) {
        return { ...voucher, info: voucher.info[0] || {} };
    }
    return voucher;
}

// ── Base pdfInfo ──────────────────────────────────────────────────────────────

/**
 * "Cancelled by / on" for a cancelled document. Only the cancel and amend paths write updatedBy, and
 * they write updatedAt as Israel wall clock in the same statement, so updatedBy being set is what
 * makes updatedAt the cancellation time. Documents cancelled before that (updatedBy empty, updatedAt
 * in UTC) get neither line rather than a wrong one.
 */
export function cancellationPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext
): Pick<BuiltPdfInfo, 'cancelledByName' | 'cancelledAtText'> {
    const by = (voucher as any).updatedBy;
    if (voucher.status !== 'Cancel' || !by) return {};
    return {
        cancelledByName: context.usersById?.[by] || '',
        cancelledAtText: formatWallClock(voucher.updatedAt, 'dd/MM/yyyy HH:mm')
    };
}

export function buildBasePdfInfo(voucher: Voucher, context: PdfInfoBuilderContext): BuiltPdfInfo {
    const info = voucher.info as any;
    return {
        ...cancellationPdfInfo(voucher, context),
        companyInfo: context.companyInfo,
        userName: (typeof voucher.createdBy === 'number' || (voucher.createdBy && !isNaN(Number(voucher.createdBy)))
            ? context.usersById?.[voucher.createdBy] || info?.userName || context.userName
            : voucher.createdBy) || info?.userName || context.userName,
        voucherCertification: voucherCertificationTypesEnum.ORIGINAL,
        pdfImageSource: context.client?.companyLogoUrl || ''
    };
}

// ── Date / voucher display helpers ───────────────────────────────────────────

/**
 * The "Ref." cell of a covered document in a PRQ / Remittance table — the same
 * priority and prefixes the PRQ form's preview prints (`getRef` in
 * routes/Financial/generateDocumentsForms/formHelpers.ts).
 *
 * The builders used to read `info.supplierInvoiceNumber`, which no writer sets:
 * Supplier Reconciliation stores the supplier's invoice as the jsonb
 * `info.supplierInvoice = {number, issueDate, dueDate, vat}`. So the preview
 * shown while creating a PRQ had the number and every PDF reopened later did not.
 */
export function coveredDocumentRef(info: any): string {
    let supplierInvoice = info?.supplierInvoice;
    if (typeof supplierInvoice === 'string') {
        try {
            supplierInvoice = JSON.parse(supplierInvoice);
        } catch {
            supplierInvoice = null;
        }
    }
    if (supplierInvoice?.number) return `S: ${supplierInvoice.number}`;
    if (info?.reservationNumber) return `P: ${info.reservationNumber}`;
    if (info?.referenceNumber) return `R: ${info.referenceNumber}`;
    return '';
}

export function formatVoucherDate(dateStr: string | Date | null | undefined): string {
    return formatShortDate(dateStr, { day: '2-digit', year: '2-digit' });
}

export function formatVoucherDateTime(dateStr: string | Date | null | undefined): string {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const datePart = formatShortDate(date, { day: '2-digit', year: '2-digit' });
        const timePart = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
        return timePart !== '00:00' ? `${datePart}, ${timePart}` : datePart;
    } catch { return ''; }
}

export function calcTotalDays(startStr: string | Date | null | undefined, endStr: string | Date | null | undefined): string {
    if (!startStr || !endStr) return '';
    try {
        const start = new Date(startStr);
        const end = new Date(endStr);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
        const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? String(diffDays) : '0';
    } catch { return ''; }
}

export function calcAge(birthDate: string | Date | null | undefined): string {
    if (!birthDate) return '';
    try {
        const dob = new Date(birthDate);
        if (isNaN(dob.getTime())) return '';
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) age--;
        return String(age);
    } catch { return ''; }
}

// ── Galor board base mapping ──────────────────────────────────────────────────

const galorBoardBaseMap: Record<string, string> = {
    '329': 'Half Board', '327': 'Continental Breakfast', '107': 'Bed & Breakfast',
    '356': 'Bed & Breakfast', '357': 'Full Board', '467': 'B&B + Extra Meals',
    '358': 'Room Only', '16': 'Half Board', '15': 'Bed & Breakfast',
    '108': 'Half Board', '109': 'Full Board'
};

export function resolveBoardBase(boardBase: string | undefined): string {
    if (!boardBase) return '';
    return galorBoardBaseMap[boardBase] || boardBase;
}

// ── Rate line helper ─────────────────────────────────────────────────────────

/**
 * Build a simple rate text line like "USD Rate: 3.65000"
 * Replaces the old 3-column rate table (From | To | Rate) with a single text line.
 * Returns empty string if rate is 1 or not provided, or if currencyCode is the local currency.
 */
export function buildRateLine(
    currencyCode: string | undefined,
    rate: number | null | undefined,
    localCurrencyCode?: string
): string {
    if (!currencyCode || !rate || rate === 1) return '';
    if (localCurrencyCode && currencyCode === localCurrencyCode) return '';
    return `${currencyCode} Rate: ${taytanFormatNumberToString(rate, EXCHANGE_RATE_DECIMAL_VIEW_NUMBER)}`;
}

// ── Receipt payment-method helpers ───────────────────────────────────────────

type PaymentMethodType = 'Credit Card' | 'Cash' | 'Check' | 'Voucher' | 'Bank Transfer' | string;

/**
 * A Pelecard credit deal (CreditType 6, תשלום בקרדיט). The card company pays us
 * the whole amount and runs the customer's schedule itself, so the receipt shows
 * the number of payments — not a first/additional split, which belongs to
 * instalments (CreditType 8) only. Matches TB's "Credit Payments" column.
 */
export function isCreditDeal(paymentObj: any): boolean {
    const raw = paymentObj?.creditType;
    return raw != null && String(raw).trim() !== '' && Number(raw) === 6;
}

/**
 * Which table a card receipt prints:
 *   credit        (CreditType 6)  Credit Payments = number of payments
 *   installments  (more than one payment otherwise)  1st Payment + Add. Payments
 *   single        one payment — Amount only; a "1st Payment" would just repeat it
 */
export type CardReceiptLayout = 'credit' | 'installments' | 'single';

export function cardReceiptLayout(paymentObj: any): CardReceiptLayout {
    if (isCreditDeal(paymentObj)) return 'credit';
    const count = resolvePaymentsCount({
        planCount: paymentObj?.installmentPlan?.count,
        additionalPayments: paymentObj?.additionalPayments
    });
    return count && count > 1 ? 'installments' : 'single';
}

// A string, not a number: the PDF table formats numeric cells as money ("3.00").
export function creditPaymentsCount(paymentObj: any): string {
    const count = resolvePaymentsCount({
        planCount: paymentObj?.installmentPlan?.count,
        additionalPayments: paymentObj?.additionalPayments,
        hasCard: true
    });
    return count ? String(count) : '';
}

// Short values — dates, counts, amounts, cheque numbers — sit centred under their
// heading; text columns (F.O.P, description, bank, account) keep the reading edge:
// left in English, right in Hebrew (ReceiptPdfHe). A value cell is a row-direction
// View, so `textAlign` alone moves only the header — `justifyContent` moves the value.
const RECEIPT_CENTERED_FIELDS = new Set([
    'dueDate',
    'firstPayment',
    'additionalPayments',
    'creditPayments',
    'number',
    'amount',
    'currency'
]);

export function alignReceiptColumns<T extends { fieldName: string; style?: { [key: string]: string } }>(
    columns: T[]
): T[] {
    return columns.map((c) =>
        RECEIPT_CENTERED_FIELDS.has(c.fieldName)
            ? { ...c, style: { ...(c.style || {}), textAlign: 'center', justifyContent: 'center' } }
            : c
    );
}

export function buildReceiptColumns(
    paymentMethodType: PaymentMethodType,
    currencyCode: string,
    paymentObj?: any
): PdfTablePropsColumn[] {
    return alignReceiptColumns(receiptColumnsFor(paymentMethodType, currencyCode, paymentObj));
}

function receiptColumnsFor(
    paymentMethodType: PaymentMethodType,
    _currencyCode: string,
    paymentObj?: any
): PdfTablePropsColumn[] {
    // The standalone "Currency" column is redundant on a single-currency
    // receipt — the TOTAL line already shows the currency, and each amount
    // cell now embeds the currency code (see buildReceiptRows /
    // buildReceiptSummaryLine). Widths re-distributed to fill the freed space.
    switch (paymentMethodType) {
        case 'Credit Card': {
            const layout = cardReceiptLayout(paymentObj);
            if (layout === 'single') {
                return [
                    { title: 'F.O.P', fieldName: 'paymentMethod', style: { width: '16%' } },
                    { title: 'Description', fieldName: 'description', style: { width: '42%' } },
                    { title: 'Value Date', fieldName: 'dueDate', style: { width: '16%' } },
                    { title: 'Amount', fieldName: 'amount', style: { width: '26%' } }
                ];
            }
            if (layout === 'credit') {
                return [
                    { title: 'F.O.P', fieldName: 'paymentMethod', style: { width: '14%' } },
                    { title: 'Description', fieldName: 'description', style: { width: '30%' } },
                    { title: 'Value Date', fieldName: 'dueDate', style: { width: '14%' } },
                    { title: 'Credit Payments', fieldName: 'creditPayments', style: { width: '20%' } },
                    { title: 'Amount', fieldName: 'amount', style: { width: '22%' } }
                ];
            }
            return [
                { title: 'F.O.P', fieldName: 'paymentMethod', style: { width: '14%' } },
                { title: 'Description', fieldName: 'description', style: { width: '24%' } },
                { title: 'Value Date', fieldName: 'dueDate', style: { width: '14%' } },
                { title: '1st Payment', fieldName: 'firstPayment', style: { width: '14%' } },
                { title: 'Add. Payments', fieldName: 'additionalPayments', style: { width: '16%' } },
                { title: 'Amount', fieldName: 'amount', style: { width: '18%' } }
            ];
        }
        case 'Bank Transfer':
            return [
                { title: 'F.O.P', fieldName: 'paymentMethod', style: { width: '18%' } },
                { title: 'Bank', fieldName: 'bank', style: { width: '26%' } },
                { title: 'Account', fieldName: 'account', style: { width: '20%' } },
                { title: 'Value Date', fieldName: 'dueDate', style: { width: '14%' } },
                { title: 'Amount', fieldName: 'amount', style: { width: '22%' } }
            ];
        case 'Check':
            return [
                { title: 'F.O.P', fieldName: 'paymentMethod', style: { width: '14%' } },
                { title: 'Bank', fieldName: 'bank', style: { width: '20%' } },
                { title: 'Account', fieldName: 'account', style: { width: '18%' } },
                { title: 'Value Date', fieldName: 'dueDate', style: { width: '14%' } },
                { title: 'Check #', fieldName: 'number', style: { width: '14%' } },
                { title: 'Amount', fieldName: 'amount', style: { width: '20%' } }
            ];
        case 'Cash':
            return [
                { title: 'F.O.P', fieldName: 'paymentMethod', style: { width: '20%' } },
                { title: 'Description', fieldName: 'description', style: { width: '44%' } },
                { title: 'Amount', fieldName: 'amount', style: { width: '36%' } }
            ];
        default:
            return [
                { title: 'F.O.P', fieldName: 'paymentMethod', style: { width: '20%' } },
                { title: 'Description', fieldName: 'description', style: { width: '44%' } },
                { title: 'Amount', fieldName: 'amount', style: { width: '36%' } }
            ];
    }
}

// Embed the currency code in each amount cell (e.g. "1.00 USD") so the
// standalone Currency column can be dropped. Also covers the F.O.P row
// firstPayment column.
function withCurrency(value: string, code: string): string {
    return value ? `${value} ${code}` : value;
}

export function buildReceiptRows(
    paymentMethodType: PaymentMethodType,
    paymentObj: any,
    amount: number,
    currencyCode: string
): PdfTablePropsRow[] {
    const formattedAmount = withCurrency(taytanFormatNumberToString(amount), currencyCode);

    switch (paymentMethodType) {
        case 'Credit Card':
            if (isCreditDeal(paymentObj)) {
                return [{
                    paymentMethod: paymentMethodType,
                    description: paymentObj?.description || paymentObj?.reference || '',
                    dueDate: formatVoucherDate(paymentObj?.valueDate),
                    creditPayments: creditPaymentsCount(paymentObj),
                    amount: formattedAmount
                }];
            }
            return [{
                paymentMethod: paymentMethodType,
                // Prefer the dedicated description field (used by the
                // tour-registration receipt for "Tour name · dates"); fall
                // back to reference for legacy / admin-created receipts that
                // only set reference.
                description: paymentObj?.description || paymentObj?.reference || '',
                dueDate: formatVoucherDate(paymentObj?.valueDate),
                firstPayment: paymentObj?.firstPayment
                    ? withCurrency(taytanFormatNumberToString(paymentObj.firstPayment), currencyCode)
                    : '',
                additionalPayments: paymentObj?.additionalPayments || '',
                amount: formattedAmount
            }];
        case 'Bank Transfer':
            return [{
                paymentMethod: paymentMethodType,
                bank: paymentObj?.bank || paymentObj?.remittanceTransferringBank?.bankName || '',
                account: paymentObj?.account || paymentObj?.remittanceTransferringBank?.accountNumber || '',
                dueDate: formatVoucherDate(paymentObj?.valueDate),
                amount: formattedAmount
            }];
        case 'Check': {
            const rawChecks = paymentObj?.checks;
            // Normalize: imported data stores checks as single object, app-created as array
            const checksArr = Array.isArray(rawChecks) ? rawChecks
                : rawChecks && typeof rawChecks === 'object' ? [rawChecks]
                : [];

            if (checksArr.length > 0) {
                return checksArr.map((check: any) => ({
                    paymentMethod: paymentMethodType,
                    bank: check?.bank || check?.bankCode || check?.bankName || '',
                    account: check?.account || check?.accountNumber || '',
                    dueDate: formatVoucherDate(check?.valueDate || paymentObj?.valueDate),
                    number: check?.number || check?.checkNumber || '',
                    amount: withCurrency(
                        taytanFormatNumberToString(Number(check?.amount) || amount),
                        currencyCode
                    )
                }));
            }
            // Fallback: no checks object, use top-level paymentObj fields
            return [{
                paymentMethod: paymentMethodType,
                bank: paymentObj?.bankName || '',
                account: paymentObj?.accountNumber || '',
                dueDate: formatVoucherDate(paymentObj?.valueDate),
                number: '',
                amount: formattedAmount
            }];
        }
        case 'Cash':
            return [{
                paymentMethod: paymentMethodType,
                description: paymentObj?.details || '',
                amount: formattedAmount
            }];
        default:
            return [{
                paymentMethod: paymentMethodType,
                description: '',
                amount: formattedAmount
            }];
    }
}

export function buildReceiptSummaryLine(
    paymentMethodType: PaymentMethodType,
    amount: number,
    currencyCode: string,
    currencySymbol: string
): PdfTablePropsRow {
    const formattedTotal = taytanFormatNumberToString(amount) + (currencySymbol || '');

    switch (paymentMethodType) {
        case 'Credit Card':
            return {
                paymentMethod: `TOTAL (${currencyCode})`,
                description: '', dueDate: '', firstPayment: '', additionalPayments: '', creditPayments: '',
                amount: formattedTotal,
                currency: currencyCode
            };
        case 'Bank Transfer':
            return {
                paymentMethod: `TOTAL (${currencyCode})`,
                bank: '', account: '', dueDate: '',
                amount: formattedTotal,
                currency: currencyCode
            };
        case 'Check':
            return {
                paymentMethod: `TOTAL (${currencyCode})`,
                bank: '', account: '', dueDate: '', number: '',
                amount: formattedTotal,
                currency: currencyCode
            };
        case 'Cash':
            return {
                paymentMethod: `TOTAL (${currencyCode})`,
                description: '',
                amount: formattedTotal,
                currency: currencyCode
            };
        default:
            return {
                paymentMethod: `TOTAL (${currencyCode})`,
                description: '',
                amount: formattedTotal,
                currency: currencyCode
            };
    }
}
